/**
 * Higher-Order Components for Async Data Management
 *
 * Provides consistent patterns for wrapping components with async data loading,
 * error boundaries, and loading states.
 */

import {
  FeatureLoading,
  LoadingSkeleton,
  LoadingSpinner,
} from "@/components/atoms/LoadingStates/LoadingStates"
import { ErrorDisplay } from "@/components/molecules/ErrorDisplay"
import { type QueryStateOptions, useQueryState } from "@/hooks/useQueryState"
import type { AppErrorClass } from "@/utils/errorHandler"
import { Box, Text, VStack } from "@chakra-ui/react"
import type { UseQueryResult } from "@tanstack/react-query"
import type React from "react"
import { FiAlertCircle, FiDatabase, FiRefreshCw } from "react-icons/fi"

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

interface AsyncDataConfig {
  level?: "page" | "feature" | "component"
  showEmptyState?: boolean
  emptyStateMessage?: string
  emptyStateAction?: React.ReactNode
  loadingVariant?: "spinner" | "skeleton"
  skeletonVariant?: "text" | "card" | "table"
  queryOptions?: QueryStateOptions
}

interface WithQueryDataProps<TData> {
  query: UseQueryResult<TData, unknown>
  children: (data: TData) => React.ReactNode
  config?: AsyncDataConfig
}

// =============================================================================
// ASYNC DATA WRAPPER COMPONENT
// =============================================================================

/**
 * Wrapper component that handles all async states for queries
 */
function AsyncDataWrapper<TData = unknown>({
  query,
  children,
  config = {},
}: WithQueryDataProps<TData>) {
  const {
    level = "feature",
    showEmptyState = true,
    emptyStateMessage = "No data found",
    emptyStateAction,
    loadingVariant = "spinner",
    skeletonVariant = "card",
    queryOptions = {},
  } = config

  const queryState = useQueryState(query, queryOptions)

  // Loading state
  if (queryState.isLoading) {
    if (loadingVariant === "skeleton") {
      return <LoadingSkeleton variant={skeletonVariant} />
    }

    switch (level) {
      case "page":
        return <LoadingSpinner size="lg" message="Loading content..." />
      case "feature":
        return <FeatureLoading />
      case "component":
        return <LoadingSpinner size="sm" />
      default:
        return <LoadingSpinner />
    }
  }

  // Error state
  if (queryState.isError && queryState.error) {
    return (
      <ErrorDisplay
        error={queryState.error}
        variant={level === "page" ? "page" : "alert"}
        onRetry={queryState.retry}
        showDetails={true}
      />
    )
  }

  // Empty state
  if (showEmptyState && queryState.isEmpty) {
    return (
      <EmptyState
        message={emptyStateMessage}
        action={emptyStateAction}
        level={level}
      />
    )
  }

  // Success state with data
  if (queryState.isSuccess && queryState.data) {
    return <>{children(queryState.data)}</>
  }

  // Fallback - should not happen
  return (
    <Box p={4} textAlign="center">
      <Text color="gray.500">No data available</Text>
    </Box>
  )
}

// =============================================================================
// EMPTY STATE COMPONENT
// =============================================================================

const EmptyState: React.FC<{
  message: string
  action?: React.ReactNode
  level: "page" | "feature" | "component"
}> = ({ message, action, level }) => {
  const getEmptyStateSize = () => {
    switch (level) {
      case "page":
        return { iconSize: "4xl", spacing: 6, padding: 16 }
      case "feature":
        return { iconSize: "3xl", spacing: 4, padding: 12 }
      case "component":
        return { iconSize: "2xl", spacing: 3, padding: 8 }
      default:
        return { iconSize: "2xl", spacing: 4, padding: 8 }
    }
  }

  const { iconSize, spacing, padding } = getEmptyStateSize()

  return (
    <VStack
      gap={spacing}
      textAlign="center"
      py={padding}
      px={4}
      color="gray.500"
      data-testid="empty-state"
    >
      <Box fontSize={iconSize} aria-hidden="true">
        <FiDatabase />
      </Box>
      <VStack gap={2}>
        <Text fontWeight="medium" fontSize={level === "page" ? "lg" : "md"}>
          {message}
        </Text>
        {action && <Box mt={2}>{action}</Box>}
      </VStack>
    </VStack>
  )
}

// =============================================================================
// HIGHER-ORDER COMPONENT
// =============================================================================

/**
 * HOC that wraps a component with async data handling
 */
export function withAsyncData<TData, TProps extends object>(
  Component: React.ComponentType<TProps & { data: TData }>,
  config: AsyncDataConfig = {},
) {
  const WrappedComponent: React.FC<
    TProps & { query: UseQueryResult<TData, unknown> }
  > = ({ query, ...props }) => (
    <AsyncDataWrapper query={query} config={config}>
      {(data) => <Component {...(props as TProps)} data={data} />}
    </AsyncDataWrapper>
  )

  WrappedComponent.displayName = `withAsyncData(${Component.displayName || Component.name})`

  return WrappedComponent
}

// =============================================================================
// SPECIALIZED HOCs
// =============================================================================

/**
 * HOC for page-level components with async data
 */
function withPageData<TData, TProps extends object>(
  Component: React.ComponentType<TProps & { data: TData }>,
  config?: Omit<AsyncDataConfig, "level">,
) {
  return withAsyncData(Component, { ...config, level: "page" })
}

/**
 * HOC for feature-level components with async data
 */
function withFeatureData<TData, TProps extends object>(
  Component: React.ComponentType<TProps & { data: TData }>,
  config?: Omit<AsyncDataConfig, "level">,
) {
  return withAsyncData(Component, { ...config, level: "feature" })
}

/**
 * HOC for component-level async data
 */
function withComponentData<TData, TProps extends object>(
  Component: React.ComponentType<TProps & { data: TData }>,
  config?: Omit<AsyncDataConfig, "level">,
) {
  return withAsyncData(Component, { ...config, level: "component" })
}

// =============================================================================
// RENDER PROP PATTERNS
// =============================================================================

/**
 * Render prop component for handling async data states
 */
const AsyncData = <TData,>({
  query,
  children,
  loading,
  error,
  empty,
  config = {},
}: {
  query: UseQueryResult<TData, unknown>
  children: (data: TData) => React.ReactNode
  loading?: () => React.ReactNode
  error?: (error: AppErrorClass, retry: () => void) => React.ReactNode
  empty?: () => React.ReactNode
  config?: AsyncDataConfig
}) => {
  const queryState = useQueryState(query, config.queryOptions)

  if (queryState.isLoading) {
    return <>{loading ? loading() : <LoadingSpinner />}</>
  }

  if (queryState.isError && queryState.error) {
    return (
      <>
        {error ? (
          error(queryState.error, queryState.retry)
        ) : (
          <ErrorDisplay error={queryState.error} onRetry={queryState.retry} />
        )}
      </>
    )
  }

  if (queryState.isEmpty) {
    return (
      <>
        {empty ? (
          empty()
        ) : (
          <EmptyState message="No data found" level="feature" />
        )}
      </>
    )
  }

  if (queryState.data) {
    return <>{children(queryState.data)}</>
  }

  return null
}

// =============================================================================
// QUERY STATUS INDICATOR
// =============================================================================

/**
 * Small status indicator showing query state
 */
const QueryStatusIndicator: React.FC<{
  query: UseQueryResult<any, unknown>
  showSuccess?: boolean
  size?: "sm" | "md"
}> = ({ query, showSuccess = false, size = "sm" }) => {
  const queryState = useQueryState(query)

  if (queryState.isLoading) {
    return (
      <Box display="inline-flex" alignItems="center" gap={1} fontSize={size}>
        <LoadingSpinner size="xs" />
        <Text fontSize="xs" color="blue.500">
          Loading...
        </Text>
      </Box>
    )
  }

  if (queryState.isError) {
    return (
      <Box display="inline-flex" alignItems="center" gap={1} fontSize={size}>
        <FiAlertCircle color="red" />
        <Text fontSize="xs" color="red.500">
          Error
        </Text>
      </Box>
    )
  }

  if (showSuccess && queryState.isSuccess) {
    return (
      <Box display="inline-flex" alignItems="center" gap={1} fontSize={size}>
        <FiRefreshCw color="green" />
        <Text fontSize="xs" color="green.500">
          Loaded
        </Text>
      </Box>
    )
  }

  return null
}

// =============================================================================
// EXPORTS
// =============================================================================

export default withAsyncData

export {
  AsyncDataWrapper,
  withPageData,
  withFeatureData,
  withComponentData,
  AsyncData,
  QueryStatusIndicator,
}
